import { Tag } from '@data/models/tag.model';
import { Input, OnInit, AfterViewInit, ViewChild, Directive, OnDestroy } from '@angular/core';
import { ControlValueAccessor, FormControl, Validators, Validator, ValidationErrors } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { isArray } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, share, startWith, filter, tap } from 'rxjs/operators';

import { TagService } from './tag.service';

const SYNC_CORTEX_TAG = 'sync-cortex';

/*
 This class aims at providing code reuse when implementing tag components.
 Do not use as is without first extending it.
 */
@Directive()
export class TagInput implements ControlValueAccessor, OnInit, Validator, AfterViewInit, OnDestroy {

    @Input() disabled = false;
    @Input() showCortex = true;

    @ViewChild(MatAutocompleteTrigger, { static: true }) trigger;

    // define some constants
    public readonly MIN_TAG_LENGTH = 3;
    public readonly MAX_TAG_LENGTH = 50;

    public syncCortexEnabled: boolean;
    public searchTerm: FormControl;
    public filteredTags: Observable<Tag[]>;
    public showAddMessage = false;

    private readonly MIN_TAGS = 0;
    private readonly MAX_TAGS = 20;
    private readonly TAG_PATTERN = /^[A-Za-z0-9]+[A-Za-z0-9-_:]*[A-Za-z0-9]+$/;

    private statusChangesSubscription: Subscription;
    private _tags: Array<string> = [];

    constructor(private tagService: TagService) { }

    @Input('tags')
    get tags() {
        return this._tags;
    }

    set tags(val) {
        this._tags = val;
        this.onChange(val);
        this.onTouched();
        this.verifyCortexSyncTag();
    }

    // these functions will be registered later
    onChange: any = () => { };
    onTouched: any = () => { };
    validateFn: any = () => { };


    public ngOnInit(): void {
        this.validateFn = validateTags(this.MIN_TAGS, this.MAX_TAGS, this.MIN_TAG_LENGTH, this.MAX_TAG_LENGTH, this.TAG_PATTERN);
        this.searchTerm = new FormControl(null,
            Validators.compose([Validators.pattern(this.TAG_PATTERN), Validators.maxLength(this.MAX_TAG_LENGTH), Validators.minLength(this.MIN_TAG_LENGTH)])
        );

        this.statusChangesSubscription = this.searchTerm.statusChanges.subscribe(() => {
            if (!this.searchTerm.valid) {
                this.trigger.closePanel();
                this.trigger.autocompleteDisabled = true;
            } else {
                this.trigger.autocompleteDisabled = false;
            }
        });

        this.filteredTags = this.searchTerm.valueChanges.pipe(
            debounceTime(600),
            distinctUntilChanged(),   // ignore if same as previous
            filter(() => {
                return this.searchTerm.status !== 'INVALID';
            }),
            startWith(''),

            switchMap((term) => {
                return this.tagService.fetchTags({ filter: term });
            }),
            tap((tags) => {
                this.computeShowAddMessage(tags);
            }),
            share(), // mandatory to prevent duplicate http calls
        );
    }

    public ngOnDestroy(): void {
        this.statusChangesSubscription.unsubscribe();
    }

    public ngAfterViewInit(): void {
        // Clears tag input text on blur
        this.trigger.panelClosingActions
            .subscribe(e => {
                if (!(e && e.source)) {
                    this.searchTerm.setValue(null);
                    this.trigger.closePanel();
                }
            });
    }

    public syncCortexChange(event: MatSlideToggleChange): void {
        if (event.checked) {
            return this.addTag(SYNC_CORTEX_TAG);
        }
        return this.removeTag(SYNC_CORTEX_TAG);
    }

    public addTag(tag: string): void {
        if (!this.disabled) {
            // init to empty array if necessary
            this.tags = this.tags || [];

            // do nothing if tag is already defined, else add it
            if (this.tags.indexOf(tag) === -1) {
                this.tags = this.tags.concat(tag);
            }

            // no matter what, clear value after selection
            this.searchTerm.setValue(null);
            this.verifyCortexSyncTag();
        }
    }

    public onSelectionChange(event: any, tag: string): void {
        // this is a quick fix until angular material releases proper selection events
        if (!event.isUserInput) {
            return;
        }

        this.addTag(tag);
    }

    public removeTag(tag: string): void {
        if (!this.disabled) {
            // important for angular lifecycle, don't use pull
            this.tags = this.tags.filter((t) => t !== tag);
            this.verifyCortexSyncTag();
        }
    }

    public writeValue(value): void {
        if (value !== undefined) {
            this.tags = value;
        }
    }

    public registerOnChange(fn): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn): void {
        this.onTouched = fn;
    }

    public validate(c: FormControl): (c: FormControl) => ValidationErrors {
        return this.validateFn(c);
    }

    // Allows Angular to disable the input.
    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (this.disabled) {
            this.searchTerm.disable();
        } else {
            this.searchTerm.enable();
        }
    }

    private verifyCortexSyncTag(): boolean {
        if (this._tags.includes(SYNC_CORTEX_TAG)) {
            return this.syncCortexEnabled = true;
        }
        return this.syncCortexEnabled = false;
    }

    private computeShowAddMessage(tags: Tag[]) {
        if (!this.searchTerm?.value || !this.searchTerm.valid || this.searchTerm.value === '') {
            this.showAddMessage = false;
            return;
        }

        const searchReg = new RegExp(`^${this.searchTerm.value}$`, 'i');
        const tagAlreadyExisting = tags.some((tag) => searchReg.test(tag.name));
        this.showAddMessage = !tagAlreadyExisting;
    }
}

/*
 validation factory, we could plug this on ngChanges later and allow the constants to be modified via input
 */
function validateTags(minCount: number, maxCount: number, minLength: number, maxLength: number, tagFormat: RegExp): ValidationErrors {
    return (c: FormControl) => {
        const tags = c.value;
        if (!isArray(tags)) {
            return { required: 'Cannot be null or undefined. Must be an empty array.' };
        }

        if (tags.length < minCount) {
            return { count: `Minimum number of tags requirement not met (${minCount}).` };
        }

        if (tags.length > maxCount - 1) {
            return { count: `Maximum number of tags exceeded (${maxCount}).` };
        }

        for (const tag of tags) {
            if (tag && tag.length > maxLength) {
                return { length: `Maximum tag length exceeded (${maxLength}).` };
            }
            if (tag && tag.length < minLength) {
                return { length: `Minimum tag length requirement not met (${minLength}).` };
            }
            if (!tagFormat.test) {
                return { format: 'Tag format not respected: ' + RegExp.toString() };
            }
        }
        return null;
    };
}
